import React, { useState, useEffect } from 'react'
import style from './campaign.module.css'
import { Card, List, Typography, Row, Col, Button, Space, Select, Tag, Spin, DatePicker } from 'antd';
import { ArrowRightOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Pie, PieChart, Cell, Legend } from 'recharts';
import { MdOutlineAnalytics, MdOutlineCampaign } from 'react-icons/md';
import { BsDot } from "react-icons/bs";
import { LuFileText, LuBox } from "react-icons/lu";
import { MdOutlineCancel } from "react-icons/md";
import { FiDownload } from "react-icons/fi";
import { GrAtm } from "react-icons/gr";
import { FaRegEye } from "react-icons/fa";
import { PiUserCircleFill } from "react-icons/pi";
import { FaMobile } from "react-icons/fa";
import { connect } from 'react-redux';
import * as DashboardActions from '../../store/slices/dashboard/dashboard';
import { Dispatch } from '@reduxjs/toolkit';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { hasPermission } from '../../utils/premission';
import { PERMISSIONS } from '../../utils/constant/permissions';

const { Text, Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const transactions = [
    { icon: <GrAtm size={20} color="#3fd0c9" />, title: 'ATM Withdrawal Processed', id: '#12OQKL' },
    { icon: <MdOutlineCancel size={20} color="#ff7eb3" />, title: 'Bill Payment', description: '$150 failed customer (1002)', id: '#24CCML' },
    { icon: <LuBox size={20} color="#3884ff" />, title: 'Funds Transfer Successful', id: '#01QAMB' },
    { icon: <FiDownload size={20} color="#7161ef" />, title: 'Mobile Recharge Processed', id: '#08OMLB' },
    { icon: <GrAtm size={20} color="#3fd0c9" />, title: 'Balance Inquiry Completed', description: 'Account ***1225', id: '#14OFKS' },
    { icon: <MdOutlineCancel size={20} color="#ff758c" />, title: 'Electricity Bill Payment Confirmed', id: '#20ITHP' },
    { icon: <FiDownload size={20} color="#3884ff" />, title: 'Subscription Payment Processed', id: '#08OFGK' },
];

interface CampaignData {
    title: string;
    amount: string;
    percentageChange: string;
    transactions: number;
    lastMonth: string;
    iconColor: string;
    icon: React.ReactNode;
    bgColor: string;
}

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

interface Props {
    Dashboard_Red: any;
    getCampaignStatusCounts: (payload: any) => Promise<any>;
    getAnnouncedWinnersCount: (payload: any) => Promise<any>;
    getTotalPrizeAmount: (payload: any) => Promise<any>;
    getTransactionChannelCounts: (payload?: any) => Promise<any>;
    getDashboardTransactions: (payload: any) => Promise<any>;
}

const TransactionCampaign: React.FC<Props> = ({
    Dashboard_Red,
    getCampaignStatusCounts,
    getAnnouncedWinnersCount,
    getTotalPrizeAmount,
    getTransactionChannelCounts,
    getDashboardTransactions,
}) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [channelData, setChannelData] = useState<any[]>([]);
    const [statusCounts, setStatusCounts] = useState<any>(null);
    const [winnersCount, setWinnersCount] = useState<number>(0);
    const [prizeAmount, setPrizeAmount] = useState<number>(0);
    const [campaignFilter, setCampaignFilter] = useState<string>('weekly');
    const [recentTransactions, setRecentTransactions] = useState<any[]>([]);
    const [trxLoading, setTrxLoading] = useState(false);
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);

    // Check dashboard permission
    const hasDashboardPermission = hasPermission(PERMISSIONS.DASHBOARD_VIEW);

    useEffect(() => {
        fetchDashboardData();
        fetchDashboardTransactions();
    }, [campaignFilter, dateRange]);

    // Fetch dashboard transactions with date filter
    const fetchDashboardTransactions = async () => {
        setTrxLoading(true);
        try {
            let fromDate: string;
            let toDate: string;

            if (dateRange && dateRange[0] && dateRange[1]) {
                fromDate = dateRange[0].format('YYYY-MM-DD');
                toDate = dateRange[1].format('YYYY-MM-DD');
            } else {
                toDate = dayjs().format('YYYY-MM-DD');
                if (campaignFilter === 'weekly') {
                    fromDate = dayjs().subtract(7, 'days').format('YYYY-MM-DD');
                } else if (campaignFilter === 'monthly') {
                    fromDate = dayjs().subtract(1, 'month').format('YYYY-MM-DD');
                } else {
                    fromDate = '2024-01-01';
                }
            }

            const res: any = await getDashboardTransactions({ fromDate, toDate, size: 10 });
            
            if (res?.success && res.data) {
                const content = res.data?.data?.content   
                    ?? res.data?.content                  
                    ?? (Array.isArray(res.data?.data) ? res.data.data : null)  
                    ?? (Array.isArray(res.data) ? res.data : []);              
                setRecentTransactions(content.slice(0, 10));
            }
        } catch (e) {
            console.error('Failed to fetch dashboard transactions', e);
        } finally {
            setTrxLoading(false);
        }
    };

    const fetchDashboardData = async () => {
        setLoading(true);
        
        // Calculate date range based on filter or custom date range
        let fromDate: string;
        let toDate: string;

        if (dateRange && dateRange[0] && dateRange[1]) {
            fromDate = dateRange[0].format('YYYY-MM-DD');
            toDate = dateRange[1].format('YYYY-MM-DD');
        } else {
            toDate = dayjs().format('YYYY-MM-DD');
            if (campaignFilter === 'weekly') {
                fromDate = dayjs().subtract(7, 'days').format('YYYY-MM-DD');
            } else if (campaignFilter === 'monthly') {
                fromDate = dayjs().subtract(1, 'month').format('YYYY-MM-DD');
            } else {
                fromDate = '2024-01-01';
            }
        }

        const payload = {
            fromDate,
            toDate,
        };

        try {
            await Promise.all([
                getCampaignStatusCounts(payload),
                getAnnouncedWinnersCount(payload),
                getTotalPrizeAmount(payload),
                getTransactionChannelCounts(payload),
            ]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (Dashboard_Red?.channelCounts?.data) {
            setChannelData(Array.isArray(Dashboard_Red.channelCounts.data) ? Dashboard_Red.channelCounts.data : []);
        }
        if (Dashboard_Red?.statusCounts?.data) {
            setStatusCounts(Dashboard_Red.statusCounts.data);
        }
        if (Dashboard_Red?.winnersCount?.data !== undefined) {
            setWinnersCount(typeof Dashboard_Red.winnersCount.data === 'number' ? Dashboard_Red.winnersCount.data : 0);
        }
        if (Dashboard_Red?.prizeAmount?.data !== undefined) {
            const prizeData = Dashboard_Red.prizeAmount.data;
            const amount = typeof prizeData === 'number'
                ? prizeData
                : typeof prizeData?.totalPrize === 'number'
                    ? prizeData.totalPrize
                    : 0;
            setPrizeAmount(amount);
        }
    }, [Dashboard_Red]);

    // Map channel codes to readable names
    const getChannelName = (channelCode: string): string => {
        const channelMap: { [key: string]: string } = {
            '010': 'ATM',
            '020': 'POS',
            '030': 'Mobile Banking',
            '040': 'Internet Banking',
            '050': 'Branch',
            '060': 'Call Center',
        };
        return channelMap[channelCode] || channelCode;
    };

    const datapie = channelData.map((item: any) => ({
        name: getChannelName(item.channel) || 'Unknown',
        value: typeof item.count === 'number' ? item.count : 0,
    }));

    const totalPie = datapie.reduce((acc, cur) => acc + cur.value, 0) || 1;

    const data2 = Array.isArray(statusCounts)
        ? statusCounts.map((item: any, i: number) => ({
              name: item.status,
              value: typeof item.count === 'number' ? item.count : 0,
              color: ['#0088FE', '#00C49F', '#7161ef', '#FF8042', '#FFBB28'][i % 5],
          }))
        : statusCounts
            ? Object.entries(statusCounts).map(([name, value]: [string, any], i) => ({
                  name,
                  value: typeof value === 'number' ? value : 0,
                  color: ['#0088FE', '#00C49F', '#7161ef', '#FF8042', '#FFBB28'][i % 5],
              }))
            : [];

    // Map channel data to get values for each channel
    const getChannelData = (channelCode: string) => {
        const channel = channelData.find(c => c.channel === channelCode);
        return {
            count: channel?.count || 0,
            amount: channel?.count ? (channel.count * 100) : 0 // Adjust multiplier as needed
        };
    };

    const atmData = getChannelData('010');
    const posData = getChannelData('020');
    const billPaymentData = getChannelData('030');
    const mobileBankingData = getChannelData('040');

    const campaignsData = [
        {
            title: "ATM",
            amount: `OMR ${atmData.amount.toLocaleString()}`,
            percentageChange: "19%",
            transactions: atmData.count,
            lastMonth: "Last Month",
            iconColor: "#095179",
            bgColor: "#e8f4fd",
            icon: <LuFileText size={20} color='#095179' />
        },
        {
            title: "POS",
            amount: `OMR ${posData.amount.toLocaleString()}`,
            percentageChange: "12%",
            transactions: posData.count,
            lastMonth: "Last Month",
            iconColor: "#1a9e5c",
            bgColor: "#e6f9f0",
            icon: <FaRegEye size={20} color='#1a9e5c' />
        },
        {
            title: "Bill Payment",
            amount: `OMR ${billPaymentData.amount.toLocaleString()}`,
            percentageChange: "25%",
            transactions: billPaymentData.count,
            lastMonth: "Last Month",
            iconColor: "#c58804",
            bgColor: "#fff8e1",
            icon: <PiUserCircleFill size={20} color='#c58804' />
        },
        {
            title: "Mobile Banking",
            amount: `OMR ${mobileBankingData.amount.toLocaleString()}`,
            percentageChange: "25%",
            transactions: mobileBankingData.count,
            lastMonth: "Last Month",
            iconColor: "#7161ef",
            bgColor: "#f0eeff",
            icon: <FaMobile size={20} color='#7161ef' />
        },
    ];

    return (
        <Spin spinning={loading}>
        <div className={style.container}>

            {/* Page Header */}
            <div className={style.pageHeader}>
                <div>
                    <Title className={style.pageTitle}>Dashboard</Title>
                    <div className={style.pageBreadcrumb}>
                        Home / <span>Dashboard</span>
                    </div>
                </div>
                <Space>
                    <RangePicker
                        value={dateRange}
                        onChange={(dates) => setDateRange(dates as [Dayjs, Dayjs] | null)}
                        format="DD/MM/YYYY"
                        placeholder={['From Date', 'To Date']}
                        allowClear
                        style={{ width: 280 }}
                    />
                    <Text type="secondary" style={{ fontSize: 13 }}>
                        {new Date().toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </Text>
                </Space>
            </div>

            {/* Row 1: [2x2 boxes] | [Pie Chart] | [Recent Transactions] */}
            <div className={style.statsRow}>

                {/* 2x2 stat boxes */}
                <div className={style.statsGrid}>
                    {hasPermission(PERMISSIONS.DASHBOARD_VIEW) ? (
                        campaignsData.map((campaign, index) => (
                            <Card key={index} className={style.statCard} bodyStyle={{ padding: '16px 20px' }}>
                                <div className={style.statCardInner}>
                                    <div className={style.cardTitle}>
                                        <div className={style.iconBox} style={{ background: campaign.bgColor }}>
                                            {campaign.icon}
                                        </div>
                                        <Text strong style={{ fontSize: 13, color: '#5a5f7d' }}>{campaign.title}</Text>
                                    </div>
                                    <Title className={style.statAmount}>{campaign.amount}</Title>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                        <span className={style.numTitle}><ArrowUpOutlined /> {campaign.percentageChange}</span>
                                        <span className={style.lastM}>{campaign.lastMonth}</span>
                                    </div>
                                    <Text className={style.Text}>
                                        Transactions: <strong style={{ color: '#1a1f36' }}>{campaign.transactions.toLocaleString()}</strong>
                                    </Text>
                                </div>
                            </Card>
                        ))
                    ) : (
                        campaignsData.map((campaign, index) => (
                            <Card key={index} className={style.statCard} bodyStyle={{ padding: '16px 20px' }}>
                                <div className={style.statCardInner}>
                                    <div className={style.cardTitle}>
                                        <div className={style.iconBox} style={{ background: campaign.bgColor }}>
                                            {campaign.icon}
                                        </div>
                                        <Text strong style={{ fontSize: 13, color: '#5a5f7d' }}>{campaign.title}</Text>
                                    </div>
                                    <Title className={style.statAmount}>OMR 0</Title>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                        <span className={style.numTitle}><ArrowUpOutlined /> 0%</span>
                                        <span className={style.lastM}>{campaign.lastMonth}</span>
                                    </div>
                                    <Text className={style.Text}>
                                        Transactions: <strong style={{ color: '#1a1f36' }}>0</strong>
                                    </Text>
                                </div>
                            </Card>
                        ))
                    )}
                </div>

                {/* Channel Wise Analytics */}
                <Card className={style.chartCard} bodyStyle={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* Header */}
                    <div className={style.chartCardHeader}>
                        <div className={style.chartCardTitle}>
                            <MdOutlineAnalytics size={20} color="#095179" />
                            Channel Wise Analytics
                        </div>
                        <Select defaultValue="lastMonth" style={{ width: 120 }} size="small">
                            <Option value="lastMonth">Last month</Option>
                            <Option value="thisMonth">This month</Option>
                        </Select>
                    </div>
                    {hasPermission(PERMISSIONS.DASHBOARD_VIEW)  ? (
                        <>
                            {/* Legend 2x2 grid */}
                            <div className={style.legendGrid}>
                                {datapie.map((item, i) => (
                                    <div key={i} className={style.legendItem}>
                                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: COLORS[i], display: 'inline-block', flexShrink: 0 }} />
                                        <span>{item.name}</span>
                                        <span style={{ marginLeft: 'auto', fontWeight: 600, color: '#1a1f36' }}>{((item.value / totalPie) * 100).toFixed(1)}%</span>
                                    </div>
                                ))}
                            </div>
                            {/* Centered Pie */}
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Tooltip formatter={(value: number) => [`${((value / totalPie) * 100).toFixed(1)}%`, '']} />
                                        <Pie
                                            data={datapie}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={75}
                                            outerRadius={110}
                                            paddingAngle={4}
                                            dataKey="value"
                                        >
                                            {datapie.map((_, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </>
                    ) : (
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8c8fa3' }}>
                            <Text type="secondary">No data available</Text>
                        </div>
                    )}
                </Card>

                {/* Recent Transactions */}
                <Card
                    className={style.recentCard}
                    title={
                        <Row align="middle" style={{ gap: 8 }}>
                            <LuBox size={18} color="#095179" />
                            <Text strong style={{ fontSize: 15, color: '#1a1f36' }}>Recent Transactions</Text>
                        </Row>
                    }
                    extra={
                        <Button 
                            type="link" 
                            style={{ color: '#095179', fontWeight: 600, fontSize: 13 }}
                            onClick={() => navigate('/transaction')}
                        >
                            View All
                        </Button>
                    }
                    bodyStyle={{ padding: '0 16px' }}
                >
                    {hasPermission(PERMISSIONS.DASHBOARD_VIEW)  ? (
                        <Spin spinning={trxLoading}>
                            <List
                                itemLayout="horizontal"
                                dataSource={recentTransactions.length > 0 ? recentTransactions : []}
                                locale={{ emptyText: trxLoading ? ' ' : 'No transactions found' }}
                                renderItem={(item: any) => (
                                    <List.Item style={{ padding: '10px 0', borderBottom: '1px solid #f4f6fb' }}>
                                        <div className={style.content}>
                                            <div className={style.txIconBox}>
                                                <LuBox size={20} color="#095179" />
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <Text className={style.txTitle}>
                                                    {item.accountShortName || item.customerIdentifier || 'Transaction'}
                                                </Text>
                                                {item.branchIdentifier && (
                                                    <Text style={{ fontSize: 11, color: '#8c8fa3', display: 'block' }}>
                                                        Branch: {item.branchIdentifier}
                                                    </Text>
                                                )}
                                                {item.transactionAmount != null && (
                                                    <Text style={{ fontSize: 11, color: '#8c8fa3', display: 'block' }}>
                                                        Amount: {item.transactionAmount}
                                                    </Text>
                                                )}
                                                <Text className={style.txId}>
                                                    #{item.bankTransactionId || item.id || '-'}
                                                </Text>
                                            </div>
                                            {item.transactionDate && (
                                                <Text style={{ fontSize: 11, color: '#8c8fa3', whiteSpace: 'nowrap' }}>
                                                    {dayjs(item.transactionDate).format('DD MMM')}
                                                </Text>
                                            )}
                                        </div>
                                    </List.Item>
                                )}
                            />
                        </Spin>
                    ) : (
                        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                            <Text type="secondary">No data available</Text>
                        </div>
                    )}
                </Card>
            </div>

            {/* Row 2: Total Campaigns full width */}
            <div className={style.mainRow}>
                <Card className={style.chartCard} bodyStyle={{ padding: '20px 24px' }}>
                    <div className={style.chartCardHeader}>
                        <div className={style.chartCardTitle}>
                            <MdOutlineCampaign size={20} color="#095179" />
                            Total Campaigns
                        </div>
                        <Select 
                            value={campaignFilter} 
                            onChange={(value) => setCampaignFilter(value)}
                            style={{ width: 110 }} 
                            size="small"
                        >
                            <Option value="weekly">Weekly</Option>
                            <Option value="monthly">Monthly</Option>
                        </Select>
                    </div>
                    {hasPermission(PERMISSIONS.DASHBOARD_VIEW) ? (
                        <>
                            <div style={{ display: 'flex', gap: 24, marginBottom: 16 }}>
                                {data2.map((item, i) => (
                                    <div key={i} style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: 11, color: '#8c8fa3', marginBottom: 2 }}>{item.name}</div>
                                        <div style={{ fontSize: 18, fontWeight: 700, color: item.color }}>{item.value}</div>
                                    </div>
                                ))}
                                <div style={{ textAlign: 'center', marginLeft: 'auto' }}>
                                    <div style={{ fontSize: 11, color: '#8c8fa3', marginBottom: 2 }}>Total Prize Amount</div>
                                    <div style={{ fontSize: 18, fontWeight: 700, color: '#095179' }}>OMR {prizeAmount.toLocaleString()}</div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: 11, color: '#8c8fa3', marginBottom: 2 }}>Announced Winners</div>
                                    <div style={{ fontSize: 18, fontWeight: 700, color: '#1a9e5c' }}>{winnersCount.toLocaleString()}</div>
                                </div>
                            </div>
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart data={data2} barSize={60}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#8c8fa3' }} axisLine={false} tickLine={false} />
                                    <YAxis domain={[0, 500]} tick={{ fontSize: 12, fill: '#8c8fa3' }} axisLine={false} tickLine={false} />
                                    <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                                    <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                                        {data2.map((entry, index) => (
                                            <Cell key={`bar-${index}`} fill={entry.color} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </>
                    ) : (
                        <div style={{ padding: '60px 20px', textAlign: 'center' }}>
                            <Text type="secondary">No data available</Text>
                        </div>
                    )}
                </Card>
            </div>
        </div>
        </Spin>
    );
};

function mapStateToProps(state: any) {
    return { Dashboard_Red: state.Dashboard_Red };
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
    ...Object.fromEntries(
        Object.entries(DashboardActions).map(([key, fn]) => [key, (...args: any[]) => dispatch((fn as any)(...args))])
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(TransactionCampaign);
